import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight, Award, Package, Star, ChevronRight, Zap, Phone, MessageCircle } from 'lucide-react';
import './Home.css';

/* ── Smart image: auto-detects jpg/jpeg/png/webp ── */
const EXTS = ['jpg','jpeg','png','webp'];
function ProductImage({ name, alt, className, style }) {
  const [idx, setIdx] = React.useState(0);
  const [failed, setFailed] = React.useState(false);
  const base = process.env.PUBLIC_URL + '/products/' + name;
  if (failed) return (
    <div className={className} style={{...style,background:'var(--off)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:32}}>🖼️</div>
  );
  return (
    <img src={`${base}.${EXTS[idx]}`} alt={alt} className={className} style={style} loading="lazy"
      onError={()=>{ idx < EXTS.length-1 ? setIdx(idx+1) : setFailed(true); }}/>
  );
}



function Counter({ to, suffix='' }) {
  const [v, setV] = useState(0);
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return; done.current = true;
    let cur=0; const step=to/60;
    const t=setInterval(()=>{ cur=Math.min(cur+step,to); setV(Math.floor(cur)); if(cur>=to)clearInterval(t); },20);
    return ()=>clearInterval(t);
  }, [to]);
  return <>{v}{suffix}</>;
}

const PRODUCTS_PREVIEW = [
  { n:'Roasted Channa',       img:'channa_roasted',  cat:'Food & Spices', tag:'Best Seller' },
  { n:'Organic Jaggery',      img:'jaggery',          cat:'Food & Spices', tag:'Organic'    },
  { n:'Kolhapuri Masala',     img:'kolhapuri_masala', cat:'Food & Spices', tag:'Signature'  },
  { n:'Silk Saree',           img:'silk_saree',       cat:'Textiles',      tag:'Handcrafted'},
  { n:'Cotton Fabric',        img:'cotton_fabric',    cat:'Textiles',      tag:'Premium'    },
  { n:'Resin Art',            img:'resin_art',        cat:'Handicrafts',   tag:'Handmade'   },
];

const NUMBERS = [
  { to:11, sfx:'+', label:'Food Products',  sub:'Fresh & authentic' },
  { to:4,  sfx:'',  label:'Textile Items',  sub:'Quality fabrics'   },
  { to:3,  sfx:'',  label:'Categories',     sub:'Food, Textile, Craft' },
  { to:100,sfx:'%', label:'Quality Check',  sub:'Every product'    },
];

const SECTORS = [
  '🫘 Roasted Channa','🟡 Turmeric Channa','🌿 Indian Spices','🌶️ Kolhapuri Masala',
  '🫙 Nagari Black Masala','🍯 Organic Jaggery','🌾 Atta (All Types)',
  '🧵 Cotton Fabric','🧶 Cotton Yarn','👗 Silk Saree','👘 Paithani Saree','🎨 Resin Art',
];

const TESTI_ALL = [
  { name:'Anjali Deshmukh',   co:'Pune, Maharashtra',      flag:'🇮🇳', q:'Yashraj Traders is our go-to for Kolhapuri masala and organic jaggery. Quality is consistently excellent, delivery always on time.',              s:5, i:'A', clr:'var(--fire)', intl:false },
  { name:'Ramesh Patil',      co:'Mumbai, Maharashtra',    flag:'🇮🇳', q:'Ordered cotton fabric in bulk — great quality, delivered on time and Rhutuja was extremely helpful throughout. Highly recommended.',              s:5, i:'R', clr:'var(--sky)',  intl:false },
  { name:'Sunita Mehta',      co:'Nashik, Maharashtra',    flag:'🇮🇳', q:'The resin art pieces are absolutely beautiful. Custom orders were handled perfectly. Will definitely order again and again.',                       s:5, i:'S', clr:'#84CC16',    intl:false },
  { name:'Fatima Al-Rashidi', co:'Dubai, UAE',             flag:'🇦🇪', q:'We import Yashraj organic jaggery and Indian spices regularly to Dubai. Packaging is excellent, quality never disappoints. Very reliable partner.', s:5, i:'F', clr:'#C0840A',    intl:true  },
  { name:'James Whitfield',   co:'London, United Kingdom', flag:'🇬🇧', q:'Sourced Silk Sarees and Cotton Fabric for our boutique through Yashraj Traders. Exceptional craftsmanship and very smooth export process.',         s:5, i:'J', clr:'#1D4ED8',    intl:true  },
  { name:'Priya Nair',        co:'Singapore',              flag:'🇸🇬', q:'The Kolhapuri masala and roasted channa were a big hit here in Singapore. Great communication and fast international shipping.',                    s:5, i:'P', clr:'#0891B2',    intl:true  },
];

const MARQUEE = [
  '🫘 Roasted Channa','🧵 Cotton Fabric','🎨 Resin Art','🍯 Organic Jaggery',
  '🌿 Indian Spices','👗 Silk Saree','🌶️ Kolhapuri Masala','🧶 Cotton Yarn',
  '🫘 Roasted Channa','🧵 Cotton Fabric','🎨 Resin Art','🍯 Organic Jaggery',
  '🌿 Indian Spices','👗 Silk Saree','🌶️ Kolhapuri Masala','🧶 Cotton Yarn',
];

export default function Home() {
  const [testiTab, setTestiTab] = useState('all');
  const testiList = testiTab==='india' ? TESTI_ALL.filter(t=>!t.intl) : testiTab==='intl' ? TESTI_ALL.filter(t=>t.intl) : TESTI_ALL;

  return (
    <div className="home page-enter">

      {/* ══ HERO ══ */}
      <section className="hero">
        <div className="hero__left">
          <div className="hero__left-inner">
            <div className="tag hero__tag"><span className="hero__tag-dot"/>Pune, Maharashtra · Est. 2016</div>
            <h1 className="hero__h1">
              <span>Quality Indian</span>
              <span className="hero__h1-accent">Products.</span>
              <span className="hero__h1-light">Delivered Honestly.</span>
            </h1>
            <p className="hero__desc">Yashraj Traders India brings authentic food products, quality textiles and handcrafted goods from the heart of Maharashtra — to customers across India and beyond.</p>
            <div className="hero__btns">
              <Link to="/products" className="btn btn-fire hero__btn-main">View Products <ArrowRight size={15}/></Link>
              <Link to="/contact" className="btn btn-outline-white">Get a Quote</Link>
            </div>
            <div className="hero__trust">
              <span>Reach us:</span>
              <div className="hero__flags">
                <a href="tel:+917775892822" className="hero__contact-pill"><Phone size={12}/> Call</a>
                <a href="https://wa.me/919226318469" className="hero__contact-pill hero__contact-pill--wa" target="_blank" rel="noreferrer"><MessageCircle size={12}/> WhatsApp</a>
              </div>
            </div>
          </div>
        </div>
        <div className="hero__right">
          <div className="hero__logo-display">
            <img src={process.env.PUBLIC_URL+'/logo.png'} alt="Yashraj Traders India" className="hero__logo-img"/>
          </div>
          <div className="hero__numbers">
            {NUMBERS.map((n,i)=>(
              <div key={i} className="hero__num-box">
                <div className="hero__num-val"><Counter to={n.to} suffix={n.sfx}/></div>
                <div className="hero__num-label">{n.label}</div>
                <div className="hero__num-sub">{n.sub}</div>
              </div>
            ))}
          </div>
          <div className="hero__badges-row">
            <div className="hero__badge-card hero__badge-card--fire"><Package size={18}/><div><div className="hero__badge-val">Food · Textile · Craft</div><div className="hero__badge-lbl">3 Product Categories</div></div></div>
            <div className="hero__badge-card hero__badge-card--dark"><Award size={18}/><div><div className="hero__badge-val">GST Verified</div><div className="hero__badge-lbl">27CBFPB6539R1Z2</div></div></div>
          </div>
        </div>
      </section>

      {/* ══ TICKER ══ */}
      <div className="ticker">
        <div className="ticker__track">
          {MARQUEE.map((item,i)=><span key={i} className="ticker__item">{item}</span>)}
        </div>
      </div>

      {/* ══ PRODUCTS PREVIEW — with real images ══ */}
      <section className="prod-preview">
        <div className="container">
          <div className="prod-preview__head">
            <div>
              <div className="tag" style={{marginBottom:14}}>Our Products</div>
              <h2 className="display">Products <em>From India</em></h2>
              <p className="body-md" style={{marginTop:10,maxWidth:480}}>Authentic food, quality textiles and unique handcrafted art — sourced directly from the best producers across India.</p>
            </div>
            <Link to="/products" className="btn btn-outline prod-preview__all">View All <ArrowUpRight size={14}/></Link>
          </div>
          <div className="prod-preview__grid">
            {PRODUCTS_PREVIEW.map((p,i)=>(
              <Link to="/products" key={i} className="prod-prev-card">
                <div className="prod-prev-card__img-wrap">
                  <ProductImage name={p.img} alt={p.n} className="prod-prev-card__img"/>
                </div>
                <div className="prod-prev-card__info">
                  <div className="prod-prev-card__cat">{p.cat}</div>
                  <div className="prod-prev-card__name">{p.n}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ══ ABOUT STRIP ══ */}
      <section className="about-band">
        <div className="about-band__bg"/>
        <div className="container about-band__layout">
          <div className="about-band__left">
            <div className="tag tag--border" style={{color:'var(--fire)',borderColor:'var(--fire)',marginBottom:20}}>Yashraj Traders India</div>
            <h2 className="display display--white" style={{marginBottom:20}}>Authentic Indian <em>Trade & Commerce</em></h2>
            <p style={{fontSize:16,color:'rgba(255,255,255,0.62)',lineHeight:1.82,marginBottom:28,maxWidth:460}}>Founded by <strong style={{color:'#FF9966'}}>Rhutuja B.</strong> and headquartered in Pune, Maharashtra, Yashraj Traders India is a GST-registered trading company delivering authentic food products, quality textiles and unique handicrafts across India and to global markets.</p>
            <Link to="/about" className="btn btn-white">Our Story <ArrowRight size={15}/></Link>
          </div>
          <div className="about-band__right">
            <div className="about-stats">
              {[{n:'Pune, MH',l:'Headquarters',s:'Maharashtra, India'},{n:'Rhutuja B.',l:'Founder',s:'Yashraj Traders India'},{n:'GST ✓',l:'Registered',s:'27CBFPB6539R1Z2'},{n:'3',l:'Sectors',s:'Food · Textile · Handicraft'}].map((s,i)=>(
                <div key={i} className="about-stat">
                  <div className="about-stat__n">{s.n}</div>
                  <div className="about-stat__l">{s.l}</div>
                  <div className="about-stat__s">{s.s}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ══ SECTORS ══ */}
      <section className="sectors-sec">
        <div className="container">
          <div className="sectors-sec__head">
            <div className="tag tag--sky" style={{marginBottom:14}}>Full Range</div>
            <h2 className="display">Everything We <em>Trade</em></h2>
          </div>
          <div className="sectors-wrap">
            {SECTORS.map((s,i)=>(
              <div key={i} className="sector-chip"><span>{s}</span><ChevronRight size={12} className="sector-chip__arr"/></div>
            ))}
          </div>
          <div className="sectors-cta">
            <Link to="/products" className="btn btn-ink">View Full Product Details <ArrowRight size={15}/></Link>
          </div>
        </div>
      </section>

      {/* ══ HOW IT WORKS ══ */}
      <section className="how">
        <div className="container">
          <div className="how__head"><div className="tag" style={{marginBottom:14}}>Process</div><h2 className="display">Order in 4 Simple Steps</h2></div>
          <div className="how__grid">
            {[{n:'01',t:'Browse & Choose',d:'Explore our food, textile and handicraft range and select what you need.'},{n:'02',t:'Enquire',d:'Call, WhatsApp or email with your requirements, quantities and delivery location.'},{n:'03',t:'Confirm & Pay',d:'We confirm availability, share pricing and agree on delivery timeline.'},{n:'04',t:'Delivered to You',d:'Your order is packed with care and delivered reliably to your doorstep.'}].map((s,i,arr)=>(
              <div key={i} className="how__step">
                <div className="how__step-top"><span className="how__step-n">{s.n}</span>{i<arr.length-1&&<div className="how__step-line"/>}</div>
                <h3 className="how__step-t">{s.t}</h3>
                <p className="how__step-d">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ TESTIMONIALS ══ */}
      <section className="testi">
        <div className="container">
          <div className="testi__head">
            <div className="tag" style={{marginBottom:14,alignSelf:'center'}}>Client Testimonials</div>
            <h2 className="display" style={{textAlign:'center'}}>What Our Clients Say</h2>
            <p style={{textAlign:'center',color:'var(--text-soft)',fontSize:14,marginTop:8}}>Trusted across India and internationally 🌍</p>
          </div>
          <div className="testi__tabs">
            {[{k:'all',label:'🌍 All Clients',n:TESTI_ALL.length},{k:'india',label:'🇮🇳 India',n:TESTI_ALL.filter(t=>!t.intl).length},{k:'intl',label:'✈️ International',n:TESTI_ALL.filter(t=>t.intl).length}].map(tb=>(
              <button key={tb.k} className={`testi__tab ${testiTab===tb.k?'testi__tab--on':''}`} onClick={()=>setTestiTab(tb.k)}>
                {tb.label}<span className="testi__tab-n">{tb.n}</span>
              </button>
            ))}
          </div>
          <div className="testi__grid">
            {testiList.map((t,i)=>(
              <div key={i} className="tcard">
                <div className="tcard__top" style={{background:t.clr}}>
                  <div className="tcard__stars">{Array(t.s).fill(0).map((_,j)=><Star key={j} size={13} fill="white" color="white"/>)}</div>
                  <div style={{display:'flex',alignItems:'center',gap:8}}>
                    {t.intl&&<span className="tcard__intl">Intl.</span>}
                    <div className="tcard__av">{t.i}</div>
                  </div>
                </div>
                <div className="tcard__body">
                  <p className="tcard__q">"{t.q}"</p>
                  <div className="tcard__author">
                    <span className="tcard__flag">{t.flag}</span>
                    <div><div className="tcard__name">{t.name}</div><div className="tcard__co">{t.co}</div></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ CTA ══ */}
      <section className="cta-fire">
        <div className="container cta-fire__inner">
          <div className="cta-fire__left">
            <div className="cta-fire__tag"><Zap size={12}/> Quick Response Guaranteed</div>
            <h2 className="cta-fire__h">Ready to Place<br/>Your Order?</h2>
            <p className="cta-fire__sub">Contact Rhutuja and the Yashraj team — call, WhatsApp or email. We respond fast.</p>
          </div>
          <div className="cta-fire__btns">
            <Link to="/contact" className="btn btn-white">Send Enquiry <ArrowRight size={15}/></Link>
            <a href="https://wa.me/919226318469" className="btn btn-outline-white" target="_blank" rel="noreferrer"><MessageCircle size={15}/> WhatsApp Now</a>
          </div>
        </div>
      </section>
    </div>
  );
}
